import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useCallback, useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CashSpecialIcon,
  ExchangeIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
  WarningIcon,
} from "lola-framework-ui-test";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/QuoteInfo";
import { PayoutInfo } from "../../components/PayoutInfo";
import { Popup } from "../../components/Popup";
import {
  SelectPaymentMethodSlot,
  categoryFromPayment,
  type FlatPayoutPick,
  type PayoutMethodCategory,
} from "../../components/payout-claim/SelectPaymentMethodSlot";
import { CashPickupDetailsSlot } from "../../components/payout-claim/CashPickupDetailsSlot";
import {
  BankAccountDetailsSlot,
  type BankAccountSelection,
} from "../../components/payout-claim/BankAccountDetailsSlot";
import { PAYOUT_CLAIM_ROUTES, TITLE_COLOR } from "../../constants";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import { paymentType } from "../../interfaces/payment";
import { amountFormatter } from "../../utils";
import { formatBankAccountLabel } from "../../utils/bankAccountLabel";
import { payerCodesEquivalent } from "../../utils/payerCode";
import { offerableSavedBankAccounts } from "../../utils/offerableBankAccounts";
import type { IBankAccountPaymentMethod } from "../../interfaces/payment-method";
import {
  PAYOUT_CLAIM_MOCK_FX,
  payoutClaimBanks,
  payoutClaimDestinationCards,
  payoutClaimDestinationCountry,
  payoutClaimDestinationCurrency,
  payoutClaimEstimateOptions,
  payoutClaimMockRecipientFirstName,
  payoutClaimMockRecipientLastName,
  payoutClaimSavedBankAccounts,
  payoutClaimSendAmount,
  payoutClaimSourceCountry,
  payoutClaimSourceCurrency,
} from "../../gallery/payoutClaimMock";
import masterCardLogo from "../../assets/master_card_icon.svg";
import visaLogo from "../../assets/visa_color_icon.svg";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


type PayoutStep = "method" | "cashDetails" | "bankDetails";

/**
 * `primaryMesh` is usually a gradient string, which is invalid for `color` and
 * `border-color`. The outlined icon in the rate pill needs a solid value, so it
 * falls back to the gradient's first stop.
 */
const solidStop = (color: string) =>
  color.match(/#[0-9a-f]{3,8}|rgba?\([^)]*\)/i)?.[0] ?? color;

/**
 * Claimer receive composer (gallery). Receive-first quote, payout method picker,
 * legal name (read-only mock), confirm simulates prepare → Sending Money.
 */
export const TransferPage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme, paymentState, setPaymentState } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("payout-claim");
  const savedListsUi = useGalleryStore((s) => s.savedListsUi);

  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    dropzoneColors,
    bannerHighlightBackground,
    bannerHighlightColor,
    bannerHighlightIconColors,
  } = colorConfig as typeof colorConfig & {
    bannerHighlightBackground?: string;
    bannerHighlightColor?: string;
    bannerHighlightIconColors?: string[];
  };

  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const payoutPickerLoading = useGalleryPickerLoading(showPaymentPopup);
  const [showExcratePopup, setShowExcratePopup] = useState(false);
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  // Stand-in for the habits GET the live flow waits on, so the saved-list
  // shimmer is visible when the cash details step opens.
  const habitsLoadDelay = useGalleryPickerLoading(
    savedListsUi !== "empty" &&
      showPaymentPopup &&
      payoutStep === "cashDetails"
  );
  const [shakePayment, setShakePayment] = useState(false);
  const [bindLoading, setBindLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const sourceCountry = payoutClaimSourceCountry;
  const destinationCountry = payoutClaimDestinationCountry;
  const sourceCurrency = payoutClaimSourceCurrency;
  const destinationCurrency = payoutClaimDestinationCurrency;

  const availableCategories = useMemo(() => {
    const set = new Set<PayoutMethodCategory>();
    for (const o of payoutClaimEstimateOptions) {
      if (o.payoutMethod === "CASH_PICKUP") set.add("CASH");
      if (o.payoutMethod === "PUSH_TO_CARD") set.add("CARD");
      if (o.payoutMethod === "BANK_ACCOUNT") set.add("BANK");
    }
    return set;
  }, []);

  const bankMethodOptions = useMemo(
    () =>
      payoutClaimEstimateOptions.filter(
        (o) => o.payoutMethod === "BANK_ACCOUNT"
      ),
    []
  );
  const catalogBankPayerCodes = useMemo(
    () => payoutClaimBanks.map((b) => b.id),
    []
  );
  const offerableAccounts = useMemo(
    () =>
      offerableSavedBankAccounts(
        payoutClaimSavedBankAccounts.filter(
          (a) =>
            (a.countryCode || "").toUpperCase() ===
            destinationCountry.toUpperCase()
        ),
        bankMethodOptions,
        catalogBankPayerCodes
      ),
    [destinationCountry, bankMethodOptions, catalogBankPayerCodes]
  );
  const destinationCards = availableCategories.has("CARD")
    ? payoutClaimDestinationCards
    : [];
  const bankLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const bank of payoutClaimBanks) {
      map[bank.id.toUpperCase()] = bank.name;
    }
    return map;
  }, []);

  const selectedOption = useMemo(() => {
    if (paymentState.payment?.type === paymentType.CASH) {
      const payerId = paymentState.payment.cashPickup?.payerId;
      if (!payerId) return null;
      return (
        payoutClaimEstimateOptions.find(
          (o) =>
            o.payoutMethod === "CASH_PICKUP" &&
            payerCodesEquivalent(o.payerCode, payerId)
        ) ?? null
      );
    }
    if (paymentState.payment?.type === paymentType.BANK) {
      const bankId = paymentState.payment.bankAccount?.bankId;
      if (!bankId) return null;
      return (
        payoutClaimEstimateOptions.find(
          (o) =>
            o.payoutMethod === "BANK_ACCOUNT" &&
            payerCodesEquivalent(o.payerCode, bankId)
        ) ?? null
      );
    }
    if (paymentState.payment?.type === paymentType.CARD) {
      return (
        payoutClaimEstimateOptions.find(
          (o) => o.payoutMethod === "PUSH_TO_CARD"
        ) ?? payoutClaimEstimateOptions[0]
      );
    }
    return payoutClaimEstimateOptions[0];
  }, [paymentState.payment]);

  const receiveAmount = selectedOption?.receiveAmount ?? 1742;
  const sendAmount = selectedOption?.sendAmount ?? payoutClaimSendAmount;
  const exchangeRate = selectedOption?.customerFxRate ?? PAYOUT_CLAIM_MOCK_FX;
  const rateText = t("exchange.rate", {
    from: sourceCurrency,
    rate: exchangeRate.toFixed(4),
    to: destinationCurrency,
  });

  const cardSelectionReady =
    paymentState.payment?.type !== paymentType.CARD ||
    Boolean(paymentState.payment.cardId);

  const cashSelectionReady =
    paymentState.payment?.type !== paymentType.CASH ||
    !!paymentState.payment.cashPickup?.payerId;

  const bankSelectionReady =
    paymentState.payment?.type !== paymentType.BANK ||
    Boolean(
      paymentState.payment.bankAccount?.paymentMethodId ||
        (paymentState.payment.bankAccount?.bankId &&
          paymentState.payment.bankAccount?.accountNumber)
    );

  const payoutReady =
    Boolean(paymentState.payment) &&
    cardSelectionReady &&
    cashSelectionReady &&
    bankSelectionReady;

  const payoutPending = !payoutReady;
  const canConfirm = payoutReady;
  const showRainbow = canConfirm || bindLoading;

  const pickIcon = useCallback(
    (type: paymentType, brand: string) => {
      switch (type) {
        case paymentType.CARD: {
          let finalIcon: string | null = null;
          if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
          if (brand.toLocaleUpperCase().includes("MASTERCARD"))
            finalIcon = masterCardLogo;
          return {
            brand,
            icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
          };
        }
        case paymentType.BANK:
          return { brand, icon: <BankIcon size={20} /> };
        case paymentType.CASH:
          return {
            brand,
            icon: <CashSpecialIcon size={20} />,
          };
        default:
          return { brand, icon: <WalletIcon size={20} /> };
      }
    },
    []
  );

  const paymentSelected = useMemo(() => {
    if (!paymentState.payment) {
      return {
        brand: t("payoutClaim.selectPayoutMethod"),
        icon: <WalletIcon size={20} />,
      };
    }
    if (!cardSelectionReady || !bankSelectionReady || !cashSelectionReady) {
      return {
        brand: t("payoutClaim.selectPayoutMethod"),
        icon: pickIcon(paymentState.payment.type, paymentState.payment.brand)
          .icon,
      };
    }
    return pickIcon(paymentState.payment.type, paymentState.payment.brand);
  }, [
    paymentState.payment,
    cardSelectionReady,
    bankSelectionReady,
    cashSelectionReady,
    pickIcon,
    t,
  ]);

  const receiveSummary = t("payoutClaim.receiveSummary", {
    amount: amountFormatter(receiveAmount, destinationCurrency),
    currency: destinationCurrency,
  });

  const openMethodPicker = useCallback(() => {
    if (
      paymentState.payment?.type === paymentType.BANK &&
      !bankSelectionReady
    ) {
      setPayoutStep("bankDetails");
    } else if (
      paymentState.payment?.type === paymentType.CASH &&
      !cashSelectionReady
    ) {
      setPayoutStep("cashDetails");
    } else {
      setPayoutStep("method");
    }
    setShowPaymentPopup(true);
  }, [
    paymentState.payment?.type,
    bankSelectionReady,
    cashSelectionReady,
  ]);

  const closePaymentPopup = useCallback(() => {
    setShowPaymentPopup(false);
    setPayoutStep("method");
  }, []);

  const handleSelectCash = useCallback(() => {
    setPayoutStep("cashDetails");
  }, []);

  const handleSelectBank = useCallback(() => {
    setPayoutStep("bankDetails");
  }, []);

  const handleSelectOther = useCallback(() => {
    setPaymentState({
      payment: {
        cardId: null,
        type: paymentType.OTHER,
        brand: t("payment.other"),
      },
    });
    closePaymentPopup();
  }, [setPaymentState, t, closePaymentPopup]);

  const handleCashDetailsSubmit = useCallback(
    (result: { stateCode: string; payerId: string; payerName: string }) => {
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.CASH,
          brand: t("transfer.cashPickup", { payer: result.payerName }),
          cashPickup: {
            stateCode: result.stateCode,
            payerId: result.payerId,
            payerName: result.payerName,
          },
        },
      });
      closePaymentPopup();
    },
    [setPaymentState, t, closePaymentPopup]
  );

  const handleBankDetailsSubmit = useCallback(
    (result: BankAccountSelection) => {
      const bankLabel =
        result.bankName || result.bankId || t("payment.bankAccount");
      const tail = result.last4 || result.accountNumber?.slice(-4) || "";
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand:
            formatBankAccountLabel(result.bankName, tail) ?? bankLabel,
          bankAccount: {
            source: result.source,
            bankId: result.bankId,
            bankName: result.bankName,
            accountNumber: result.accountNumber,
            paymentMethodId: result.paymentMethodId,
            last4: result.last4 || tail || undefined,
          },
        },
      });
      closePaymentPopup();
    },
    [setPaymentState, t, closePaymentPopup]
  );

  const handleSelectCardInstrument = useCallback(
    (selection: {
      paymentMethodId: string;
      brand: string;
      last4: string;
    }) => {
      const displayName = `${(selection.brand || "").toUpperCase()}-${selection.last4}`;
      setPaymentState({
        payment: {
          cardId: selection.paymentMethodId,
          type: paymentType.CARD,
          brand: displayName,
        },
      });
      closePaymentPopup();
    },
    [setPaymentState, closePaymentPopup]
  );

  const handleSelectBankInstrument = useCallback(
    (account: IBankAccountPaymentMethod) => {
      const bankCode = (account.bankName || "").toUpperCase();
      const bankLabel = bankLabels[bankCode] || t("payment.bankAccount");
      const last4 = account.maskedAccountNumber.replace(/\D/g, "").slice(-4);
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand: formatBankAccountLabel(bankLabel, last4) ?? bankLabel,
          bankAccount: {
            source: "saved",
            paymentMethodId: account.paymentMethodId,
            bankId: bankCode,
            bankName: bankLabel,
            last4: last4 || undefined,
          },
        },
      });
      closePaymentPopup();
    },
    [bankLabels, t, setPaymentState, closePaymentPopup]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        handleSelectCardInstrument(pick.card);
        return;
      }
      if (pick.kind === "bank") {
        handleSelectBankInstrument(pick.account);
        return;
      }
      if (pick.kind === "add") {
        closePaymentPopup();
        navigate(PAYOUT_CLAIM_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectBank();
        return;
      }
      if (pick.category === "CASH") {
        handleSelectCash();
        return;
      }
      handleSelectOther();
    },
    [
      handleSelectCardInstrument,
      handleSelectBankInstrument,
      handleSelectCash,
      handleSelectBank,
      handleSelectOther,
      closePaymentPopup,
      navigate,
    ]
  );

  const handleConfirmClick = useCallback(() => {
    if (!payoutReady) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    setPrepareError(null);
    setBindLoading(true);
    window.setTimeout(() => {
      setBindLoading(false);
      navigate(PAYOUT_CLAIM_ROUTES.SENDING_MONEY);
    }, 700);
  }, [payoutReady, navigate]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(PAYOUT_CLAIM_ROUTES.TRANSACTIONS)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title
              title={t("payoutClaim.receiveTitle")}
              align="left"
              color={TITLE_COLOR}
            />
            <section className="estimated-quote-section stacked-quote">
              <section className="quote-container">
                <div className="banrural-amounts">
                  <div className="banrural-send-wrap">
                    <QuoteInfo
                      amount={amountFormatter(receiveAmount, destinationCurrency, {
                        country: "en-US",
                      })}
                      label={t("payoutClaim.youReceive")}
                      currency={destinationCurrency}
                      textColor={bodyCopy}
                      mode="info"
                      hideCurrencySymbol={destinationCurrency !== "USD"}
                      isActive
                      icon={
                        <FlagCircle iso2={destinationCountry.toLowerCase()} />
                      }
                    />
                    <span
                      className="banrural-swap"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowExcratePopup(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setShowExcratePopup(true);
                        }
                      }}
                      style={{ background: backgroundBtn }}
                    >
                      <ExchangeIcon size={18} colors={[textColorBtn, textColorBtn]} />
                    </span>
                  </div>
                  <QuoteInfo
                    label={t("payoutClaim.sentFrom")}
                    currency={sourceCurrency}
                    textColor={bodyCopy}
                    mode="info"
                    amount={amountFormatter(sendAmount, sourceCurrency, {
                      country: "en-US",
                    })}
                    icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                    bottomSlot={
                      <section
                        className="banrural-exchange-rate banrural-exchange-rate--alert"
                        role="button"
                        tabIndex={0}
                        onClick={() => setShowExcratePopup(true)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setShowExcratePopup(true);
                          }
                        }}
                        style={
                          {
                            "--bgc": bannerHighlightBackground ?? bodyCopy,
                            "--colorc": solidStop(
                              bannerHighlightIconColors?.[0] ?? title,
                            ),
                            "--fillc": bannerHighlightColor ?? title,
                          } as CSSProperties
                        }
                      >
                        <span className="banrural-exchange-rate-icon">i</span>
                        <span className="banrural-exchange-rate-text">
                          <b>{rateText}</b>
                          <label>{t("exchange.estimatedRate")}</label>
                        </span>
                      </section>
                    }
                  />
                </div>
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("payoutClaim.payoutMethod")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                background={backgroundBtn}
                color={textColorBtn}
                onChange={openMethodPicker}
                className={`${payoutPending ? "payment-required" : ""} ${
                  shakePayment ? "shake" : ""
                }`.trim()}
              />
              <section className="recipient-identity-fields">
                <BodyCopy textColor={TITLE_COLOR} className="recipient-identity-label">
                  {t("payoutClaim.yourName")}
                </BodyCopy>
                <div className="recipient-identity-inputs">
                  <input
                    type="text"
                    className="recipient-identity-input"
                    value={[
                      payoutClaimMockRecipientFirstName,
                      payoutClaimMockRecipientLastName,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    readOnly
                    placeholder={t("payoutClaim.yourName")}
                    autoComplete="name"
                  />
                </div>
              </section>
              {prepareError && (
                <BodyCopy className="payment-error-text" role="alert">
                  {prepareError}
                </BodyCopy>
              )}
              <BodyCopy className="footer-text transfer-timing-note" textColor={bodyCopy}>
                <b>{t("payoutClaim.instantReceive")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("payoutClaim.mayTakeFew")}
                </span>
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <section className="footer-summary">
              <BodyCopy className="footer-text">{receiveSummary}</BodyCopy>
            </section>
            <div
              className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}
            >
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={bindLoading || !canConfirm}
                onClick={handleConfirmClick}
              >
                {bindLoading
                  ? t("transfer.verifying")
                  : t("payoutClaim.confirmReceive")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup visible={showPaymentPopup} onClose={closePaymentPopup}>
        {payoutStep === "method" ? (
          <SelectPaymentMethodSlot
            selected={categoryFromPayment(paymentState.payment?.type)}
            selectedPaymentMethodId={
              paymentState.payment?.type === paymentType.CARD
                ? paymentState.payment.cardId
                : paymentState.payment?.type === paymentType.BANK
                  ? paymentState.payment.bankAccount?.paymentMethodId ?? null
                  : null
            }
            availableCategories={availableCategories}
            cards={destinationCards}
            accounts={availableCategories.has("BANK") ? offerableAccounts : []}
            loading={payoutPickerLoading}
            bankLabels={bankLabels}
            onPick={handlePayoutPick}
            onClose={closePaymentPopup}
          />
        ) : payoutStep === "cashDetails" ? (
          <CashPickupDetailsSlot
            options={payoutClaimEstimateOptions}
            value={paymentState.payment?.cashPickup}
            habitsBusy={savedListsUi === "loading" || habitsLoadDelay}
            habitsEmpty={savedListsUi === "empty"}
            onBack={() => setPayoutStep("method")}
            onClose={closePaymentPopup}
            onSubmit={handleCashDetailsSubmit}
          />
        ) : (
          <BankAccountDetailsSlot
            destinationCountry={destinationCountry}
            options={payoutClaimEstimateOptions}
            value={paymentState.payment?.bankAccount}
            onBack={() => setPayoutStep("method")}
            onClose={closePaymentPopup}
            onSubmit={handleBankDetailsSubmit}
          />
        )}
      </Popup>

      <Popup
        visible={showExcratePopup}
        onClose={() => setShowExcratePopup(false)}
      >
        <section className="qoute-popup">
          <WarningIcon size={60} />
          <BodyCopy textColor={bodyCopy}>
            <Trans i18nKey="transfer.quote.disclaimer" ns="payout-claim" />
          </BodyCopy>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});

/**
 * Gallery variant: Transfer with a prepare-rail error surfaced inline.
 */
export const TransferPrepareErrorPage = memo(() => {
  const { t } = useTranslation("payout-claim");
  const { theme } = useStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("payout-claim");
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } =
    colorConfig;

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className="scrollable need-help-header"
    >
      <Layout.Header>
        <Navbar
          title={t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(PAYOUT_CLAIM_ROUTES.TRANSFER)}
          onIconClick={openSupport}
          icon={<SupportIcon size={35} colors={dropzoneColors} />}
        />
      </Layout.Header>
      <Layout.Content className="transfer-section">
        <ScrollFadeIndicator color={bodyCopy} />
        <Title title={t("payoutClaim.receiveTitle")} align="left" color={TITLE_COLOR} />
        <section className="payout-container">
          <PayoutInfo
            title={t("payoutClaim.payoutMethod")}
            subtitle="VISA-1111"
            icon={<WalletIcon size={20} />}
            background={backgroundBtn}
            color={textColorBtn}
            onChange={() => {}}
          />
          <BodyCopy className="payment-error-text" role="alert">
            {t("transfer.prepare.errorProvision")}
          </BodyCopy>
          <BodyCopy className="footer-text" textColor={bodyCopy}>
            <b>{t("payoutClaim.instantReceive")}</b>
          </BodyCopy>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => navigate(PAYOUT_CLAIM_ROUTES.TRANSFER)}
          >
            {t("common.retry")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
    {supportSheet}
  </>
  );
});
